import {
  mapStringingDataToDesign,
  selectedPanelsPerRoofToDesignSelection,
  stringingUsesCanvasRoofs
} from '../production/production-mapping';
import {
  applyCanvasSelectionToDesigns,
  applySelectionToPanelGroups
} from './legacy-proposal-selection';

const toRoofIndexMap = (raw) => {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  Object.keys(raw).forEach((key) => {
    const roof = Number(key);
    if (!Number.isFinite(roof)) return;
    const indexes = Array.isArray(raw[key])
      ? raw[key].map(Number).filter(Number.isFinite)
      : [];
    if (indexes.length > 0) out[roof] = indexes;
  });
  return out;
};

const buildSyntheticStringing = (selectedPerRoof) => {
  const panels = [];
  Object.keys(selectedPerRoof).forEach((roofKey) => {
    const roof = Number(roofKey);
    if (!Number.isFinite(roof)) return;
    selectedPerRoof[roof].forEach((panel) => panels.push([roof, panel]));
  });
  return panels.length > 0 ? [{ panels, draw: false }] : null;
};

export function mergeSimulateResultIntoProposalData(
  prev,
  result,
  proposalVersion
) {
  if (!prev || !result) return prev;
  const designData = prev.design_data ?? {};
  const summary = designData.summary ?? {};
  const annualKwh =
    result.annual_kwh ?? result.total_annual_kwh ?? summary.solar_production;
  const annualUsageRaw = summary?.annual_usage ?? prev?.annual_usage;
  const annualUsage = Number(annualUsageRaw);
  const offset =
    Number.isFinite(annualUsage) && annualUsage > 0 && annualKwh != null
      ? (Number(annualKwh) / annualUsage) * 100
      : (result.offset ?? summary.offset);

  const updatedSummary = {
    ...summary,
    total_panels:
      result.panel_count ?? result.selected_panel_count ?? summary.total_panels,
    totalPanels:
      result.panel_count ?? result.selected_panel_count ?? summary.totalPanels,
    system_size:
      result.system_size_kw ?? result.system_size ?? summary.system_size,
    systemSize:
      result.system_size_kw ?? result.system_size ?? summary.systemSize,
    solar_production:
      result.annual_kwh ?? result.total_annual_kwh ?? summary.solar_production,
    solarProduction:
      result.annual_kwh ?? result.total_annual_kwh ?? summary.solarProduction,
    offset:
      offset != null
        ? typeof offset === 'number'
          ? Math.round(offset)
          : offset
        : summary.offset
  };

  const selectedPerRoof = toRoofIndexMap(
    result.selected_panels_per_roof_canvas ?? result.selected_panels_per_roof
  );
  const panels = Array.isArray(designData.panels) ? designData.panels : [];
  const designMap =
    designData.design_map && typeof designData.design_map === 'object'
      ? designData.design_map
      : null;
  let designs = designData.designs ?? prev.designs;
  if (
    Array.isArray(designs) &&
    Object.keys(selectedPerRoof).length > 0
  ) {
    designs =
      panels.length > 0
        ? applyCanvasSelectionToDesigns(
            designs,
            selectedPerRoof,
            panels,
            designMap
          )
        : designs.map((design, index) => {
            const indices = selectedPerRoof[index];
            if (!Array.isArray(indices)) return design;
            const verticalCount =
              typeof design.vertical === 'number'
                ? design.vertical
                : Array.isArray(design.vertical)
                  ? design.vertical.length
                  : 0;
            const horizontalCount =
              typeof design.horizontal === 'number'
                ? design.horizontal
                : Array.isArray(design.horizontal)
                  ? design.horizontal.length
                  : 0;
            return {
              ...design,
              ...selectedPanelsPerRoofToDesignSelection(
                indices,
                verticalCount,
                horizontalCount
              )
            };
          });
  }

  const stringsRaw = Array.isArray(result.resolved_strings)
    ? result.resolved_strings
    : null;
  const mappedStrings = mapStringingDataToDesign(
    stringsRaw,
    result.stringing_roof_index_map ?? null,
    result.stringing_selected_panel_index_map ?? null
  );
  const resolvedStrings = stringingUsesCanvasRoofs(
    mappedStrings,
    selectedPerRoof
  )
    ? mappedStrings
    : Object.keys(selectedPerRoof).length > 0
      ? buildSyntheticStringing(selectedPerRoof)
      : mappedStrings;

  const productionPerPanel =
    result.production_per_panel ??
    result.productionPerPanel ??
    designData.production_per_panel;
  const productionPerString =
    result.production_per_string ??
    result.productionPerString ??
    designData.production_per_string;
  const monthlyProduction =
    result.monthly_kwh ??
    result.monthly_production ??
    designData.monthlyProduction ??
    designData.monthly_production;

  const updatedDesignData = {
    ...designData,
    summary: updatedSummary,
    panels:
      Object.keys(selectedPerRoof).length > 0
        ? applySelectionToPanelGroups(panels, selectedPerRoof)
        : panels,
    selected_panels:
      result.panel_count ??
      result.selected_panel_count ??
      designData.selected_panels,
    selectedPanelsPerRoof:
      Object.keys(selectedPerRoof).length > 0
        ? selectedPerRoof
        : designData.selectedPanelsPerRoof,
    selected_panels_per_roof:
      Object.keys(selectedPerRoof).length > 0
        ? selectedPerRoof
        : designData.selected_panels_per_roof,
    monthly_production: monthlyProduction,
    monthlyProduction,
    stringing_data: resolvedStrings ?? designData.stringing_data,
    stringing_panel_index_mode:
      result.stringing_panel_index_mode ??
      designData.stringing_panel_index_mode ??
      'all',
    inverters_used:
      result.inverters_used ??
      result.solar_design?.[0]?.inverters_used ??
      designData.inverters_used,
    production_per_panel: productionPerPanel,
    productionPerPanel,
    production_per_string: productionPerString,
    productionPerString,
    dc_ac_ratio: result.dc_ac_ratio ?? designData.dc_ac_ratio,
    total_clipping_kwh:
      result.annual_clipping_kwh ?? designData.total_clipping_kwh,
    total_monthly_clipping_kwh: Array.isArray(result.monthly_clipping_kwh)
      ? result.monthly_clipping_kwh.join(',')
      : designData.total_monthly_clipping_kwh,
    ...(designs != null ? { designs } : {})
  };

  return {
    ...prev,
    ...(proposalVersion != null ? { version: proposalVersion } : {}),
    designs: designs ?? prev.designs,
    design_data: updatedDesignData
  };
}
